// src/components/PhotoUpload.js
// Uploads directly to Cloudinary (unsigned preset) — no backend needed.
// Requires REACT_APP_CLOUDINARY_CLOUD_NAME and REACT_APP_CLOUDINARY_UPLOAD_PRESET.
import React, { useRef, useState } from 'react';

const CLOUD_NAME    = process.env.REACT_APP_CLOUDINARY_CLOUD_NAME;
const UPLOAD_PRESET = process.env.REACT_APP_CLOUDINARY_UPLOAD_PRESET;

// Insert Cloudinary transformations into a stored URL
export const thumbUrl  = (url) => url?.replace('/upload/', '/upload/c_fill,w_80,h_80,q_auto,f_auto/');
export const detailUrl = (url) => url?.replace('/upload/', '/upload/c_limit,w_900,q_auto,f_auto/');

export default function PhotoUpload({ currentUrl, onUpload, compact = false }) {
  const inputRef           = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError]  = useState('');

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!CLOUD_NAME || !UPLOAD_PRESET) {
      setError('Cloudinary is not configured. Contact your administrator.');
      return;
    }

    setUploading(true);
    setError('');

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('upload_preset', UPLOAD_PRESET);

      const res = await fetch(
        `https://api.cloudinary.com/v1_1/${CLOUD_NAME}/image/upload`,
        { method: 'POST', body: formData }
      );

      if (!res.ok) throw new Error('Upload failed');
      const data = await res.json();
      onUpload(data.secure_url);
    } catch (err) {
      setError('Upload failed. Check your connection and try again.');
    } finally {
      setUploading(false);
      // Reset input so same file can be re-selected
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  if (compact) {
    // Compact version for Edit page — shows current photo + change button inline
    return (
      <div style={{ marginBottom: 12 }}>
        {currentUrl ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <img
              src={thumbUrl(currentUrl)}
              alt="Tool"
              style={{ width: 64, height: 64, objectFit: 'cover', borderRadius: 8, border: '1px solid #ffcccc', flexShrink: 0 }}
            />
            <div style={{ flex: 1 }}>
              <p style={{ fontSize: '0.78rem', color: '#16a34a', fontWeight: 600, marginBottom: 4 }}>Photo on file</p>
              <button
                type="button"
                onClick={() => inputRef.current?.click()}
                disabled={uploading}
                style={{ fontSize: '0.75rem', padding: '4px 10px', background: 'transparent', color: '#CC0000',
                  border: '1px solid #ffcccc', borderRadius: 6, cursor: 'pointer' }}
              >
                {uploading ? 'Uploading…' : 'Change Photo'}
              </button>
            </div>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            disabled={uploading}
            style={{ width: '100%', padding: '10px', background: 'white', color: '#CC0000',
              border: '1px dashed #ffcccc', borderRadius: 8, cursor: 'pointer',
              fontSize: '0.85rem', fontWeight: 600 }}
          >
            {uploading ? 'Uploading…' : '📷 Add Photo'}
          </button>
        )}
        {error && <p style={{ fontSize: '0.75rem', color: '#CC0000', marginTop: 4 }}>{error}</p>}
        <input ref={inputRef} type="file" accept="image/*" capture="environment"
          onChange={handleFile} style={{ display: 'none' }} />
      </div>
    );
  }

  // Full version for Add page — large camera button
  return (
    <div style={{ marginBottom: 16 }}>
      {currentUrl ? (
        <div style={{ position: 'relative', marginBottom: 4 }}>
          <img
            src={detailUrl(currentUrl)}
            alt="Tool"
            style={{ width: '100%', maxHeight: 200, objectFit: 'cover', borderRadius: 10,
              border: '2px solid var(--border-light)' }}
          />
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            disabled={uploading}
            style={{ position: 'absolute', bottom: 8, right: 8, background: 'rgba(0,0,0,0.6)',
              color: 'white', border: 'none', borderRadius: 6, padding: '6px 10px',
              fontSize: '0.78rem', cursor: 'pointer', fontWeight: 600 }}
          >
            {uploading ? 'Uploading…' : '📷 Change'}
          </button>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={uploading}
          style={{ width: '100%', minHeight: 80, background: 'var(--bg-elevated)',
            border: '2px dashed var(--border-light)', borderRadius: 10, cursor: 'pointer',
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
            gap: 6, color: 'var(--text-muted)', fontSize: '0.85rem' }}
        >
          <span style={{ fontSize: '1.8rem' }}>📷</span>
          <span style={{ fontWeight: 600 }}>{uploading ? 'Uploading…' : 'Add Photo (optional)'}</span>
          <span style={{ fontSize: '0.75rem' }}>Tap to use camera or choose from library</span>
        </button>
      )}
      {error && <p style={{ fontSize: '0.78rem', color: 'var(--status-lost)', marginTop: 4 }}>{error}</p>}
      <input ref={inputRef} type="file" accept="image/*" capture="environment"
        onChange={handleFile} style={{ display: 'none' }} />
    </div>
  );
}
